import { apiClient } from "./apiClient";

import type {
  AdminUserAddressesData,
  CreateUserInput,
  UpdateUserAccountInput,
  UpdateUserInput,
  UserDetailsData,
  UserDevicesData,
  UserStatusActionInput,
  UsersListData,
  UsersQuery,
  UserActivityData,
  AdminUserImagesData,
AddUserImageInput,
AddUserImageData,
DeleteUserImageData,
SetUserDefaultAddressData,
CreateUserAppQuestionInput,
CreateUserAppQuestionData,
SetUserLoginCredentialsInput,
SetUserLoginCredentialsData,
} from "../types/users.types";

function buildUsersQuery(
  query: UsersQuery = {},
): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) {
    params.set(
      "search",
      query.search.trim(),
    );
  }

  if (query.fullName?.trim()) {
    params.set(
      "fullName",
      query.fullName.trim(),
    );
  }

  if (query.phoneNumber?.trim()) {
    params.set(
      "phoneNumber",
      query.phoneNumber.trim(),
    );
  }

  if (query.email?.trim()) {
    params.set(
      "email",
      query.email.trim(),
    );
  }

  if (query.province?.trim()) {
    params.set(
      "province",
      query.province.trim(),
    );
  }

  if (query.accountType?.trim()) {
    params.set(
      "accountType",
      query.accountType.trim(),
    );
  }

  if (query.accountLevel?.trim()) {
    params.set(
      "accountLevel",
      query.accountLevel.trim(),
    );
  }

  if (query.status) {
    params.set(
      "status",
      query.status,
    );
  }

  if (query.registeredFrom?.trim()) {
    params.set(
      "registeredFrom",
      query.registeredFrom.trim(),
    );
  }

  if (query.registeredTo?.trim()) {
    params.set(
      "registeredTo",
      query.registeredTo.trim(),
    );
  }

  if (query.lastActivityFrom?.trim()) {
    params.set(
      "lastActivityFrom",
      query.lastActivityFrom.trim(),
    );
  }

  if (query.lastActivityTo?.trim()) {
    params.set(
      "lastActivityTo",
      query.lastActivityTo.trim(),
    );
  }

  if (query.sort) {
    params.set(
      "sort",
      query.sort,
    );
  }

  if (query.limit !== undefined) {
    params.set(
      "limit",
      String(query.limit),
    );
  }

  if (query.cursor?.trim()) {
    params.set(
      "cursor",
      query.cursor.trim(),
    );
  }

  const queryString =
    params.toString();

  return queryString
    ? `/users?${queryString}`
    : "/users";
}

function buildActivityQuery(input?: {
  type?: string;
  limit?: number;
  cursor?: string;
}): string {
  if (!input) {
    return "";
  }

  const params = new URLSearchParams();

  if (input.type?.trim()) {
    params.set(
      "type",
      input.type.trim(),
    );
  }

  if (input.limit !== undefined) {
    params.set(
      "limit",
      String(input.limit),
    );
  }

  if (input.cursor?.trim()) {
    params.set(
      "cursor",
      input.cursor.trim(),
    );
  }

  const queryString =
    params.toString();

  return queryString
    ? `?${queryString}`
    : "";
}

export const usersApi = {
  list(
    query: UsersQuery = {},
  ) {
    return apiClient.get<UsersListData>(
      buildUsersQuery(query),
    );
  },

  getById(
    uid: string,
  ) {
    return apiClient.get<UserDetailsData>(
      `/users/${encodeURIComponent(uid)}`,
    );
  },

  create(
    input: CreateUserInput,
  ) {
    return apiClient.post<UserDetailsData>(
      "/users",
      input,
    );
  },

  update(
    uid: string,
    input: UpdateUserInput,
  ) {
    return apiClient.patch<UserDetailsData>(
      `/users/${encodeURIComponent(uid)}`,
      input,
    );
  },

  updateAccount(
    uid: string,
    input: UpdateUserAccountInput,
  ) {
    return apiClient.patch<{
      uid: string;

      before: {
        accountType: string | null;
        AccountLevel: string | null;
      };

      after: {
        accountType: string | null;
        AccountLevel: string | null;
      };

      changedFields?: string[];
    }>(
      `/users/${encodeURIComponent(uid)}/account`,
      input,
    );
  },

  block(
    uid: string,
    input: UserStatusActionInput = {},
  ) {
    return apiClient.post<UserDetailsData>(
      `/users/${encodeURIComponent(uid)}/block`,
      input,
    );
  },

  unblock(
    uid: string,
    input: UserStatusActionInput = {},
  ) {
    return apiClient.post<UserDetailsData>(
      `/users/${encodeURIComponent(uid)}/unblock`,
      input,
    );
  },

  disable(
    uid: string,
    input: UserStatusActionInput = {},
  ) {
    return apiClient.post<UserDetailsData>(
      `/users/${encodeURIComponent(uid)}/disable`,
      input,
    );
  },

  enable(
    uid: string,
    input: UserStatusActionInput = {},
  ) {
    return apiClient.post<UserDetailsData>(
      `/users/${encodeURIComponent(uid)}/enable`,
      input,
    );
  },

  getAddresses(
    uid: string,
  ) {
    return apiClient.get<AdminUserAddressesData>(
      `/users/${encodeURIComponent(uid)}/addresses`,
    );
  },

  getActivity(
    uid: string,
    query?: {
      type?: string;
      limit?: number;
      cursor?: string;
    },
  ) {
    return apiClient.get<UserActivityData>(
      `/users/${encodeURIComponent(
        uid,
      )}/activity${buildActivityQuery(query)}`,
    );
  },

  getDevices(
    uid: string,
  ) {
    return apiClient.get<UserDevicesData>(
      `/users/${encodeURIComponent(uid)}/devices`,
    );
  },

  getImages(
  uid: string,
) {
  return apiClient.get<AdminUserImagesData>(
    `/users/${encodeURIComponent(uid)}/images`,
  );
},

addImage(
  uid: string,
  input: AddUserImageInput,
) {
  return apiClient.post<AddUserImageData>(
    `/users/${encodeURIComponent(uid)}/images`,
    input,
  );
},

deleteImage(
  uid: string,
  imageId: string,
) {
  return apiClient.delete<DeleteUserImageData>(
    `/users/${encodeURIComponent(uid)}/images/${encodeURIComponent(
      imageId,
    )}`,
  );
},

setDefaultAddress(
  uid: string,
  addressId: string,
) {
  return apiClient.post<SetUserDefaultAddressData>(
    `/users/${encodeURIComponent(
      uid,
    )}/addresses/${encodeURIComponent(
      addressId,
    )}/default`,
    {},
  );
},

createAppQuestion(
  uid: string,
  input: CreateUserAppQuestionInput,
) {
  return apiClient.post<CreateUserAppQuestionData>(
    `/users/${encodeURIComponent(
      uid,
    )}/app-questions`,
    input,
  );
},

  setLoginCredentials(
    uid: string,
    input: SetUserLoginCredentialsInput,
  ) {
    return apiClient.post<SetUserLoginCredentialsData>(
      `/users/${encodeURIComponent(
        uid,
      )}/auth-credentials`,
      input,
    );
  },
  
};